import React, { useState, useCallback, useMemo, useRef } from 'react'
import * as Sentry from '@sentry/react'
import type { ProjectLabel } from '@bandos/shared'
import * as LabelService from '../../services/labels/labelService'
import { App } from 'antd'
import { LabelContext, type LabelContextType } from './LabelContextDef'

export function LabelProvider({ children }: { children: React.ReactNode }) {
  const [labels, setLabels] = useState<ProjectLabel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const { notification } = App.useApp()

  // Store notification in ref to avoid callback recreation on every render
  // (App.useApp() returns a new object each render, breaking memoization)
  const notificationRef = useRef(notification)
  notificationRef.current = notification

  // Track labels for each node (node_id -> labels[]) - using state to trigger re-renders
  const [nodeLabelsMap, setNodeLabelsMap] = useState<Map<string, ProjectLabel[]>>(new Map())
  const [nodeLabelsVersion, setNodeLabelsVersion] = useState(0) // Version counter to trigger re-renders

  /**
   * Set project labels directly (used for public/read-only mode)
   */
  const setLabelsDirectly = useCallback((newLabels: ProjectLabel[]) => {
    setLabels(newLabels)
  }, [])

  /**
   * Load all project labels for a mindmap
   */
  const loadLabels = useCallback(async (mindmapId: string) => {
    setIsLoading(true)
    try {
      const { data, error } = await LabelService.getProjectLabels(mindmapId)

      if (error) {
        notificationRef.current.error({
          title: 'Failed to load labels',
          description: error.message,
        })
        return
      }

      if (data) {
        setLabels(data.labels)
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'label', action: 'load_labels' },
      })
      notificationRef.current.error({
        title: 'Failed to load labels',
        description: 'An unexpected error occurred',
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Create a new project label
   */
  const createLabel = useCallback(
    async (mindmapId: string, labelText: string, color: string): Promise<ProjectLabel | null> => {
      const { data, error } = await LabelService.createProjectLabel(mindmapId, labelText, color)

      if (error) {
        notificationRef.current.error({
          title: 'Failed to create label',
          description: error.message,
        })
        return null
      }

      if (data) {
        // Add new label to the list
        setLabels((prev) => [...prev, data])
        return data
      }

      return null
    },
    []
  )

  /**
   * Delete a project label (removes it from all nodes)
   */
  const deleteLabel = useCallback(async (mindmapId: string, labelId: string): Promise<boolean> => {
    const { error } = await LabelService.deleteProjectLabel(mindmapId, labelId)

    if (error) {
      notificationRef.current.error({
        title: 'Failed to delete label',
        description: error.message,
      })
      return false
    }

    // Remove label from the list
    setLabels((prev) => prev.filter((l) => l.id !== labelId))

    // Remove label from all nodes in the cache
    setNodeLabelsMap((prev) => {
      const updated = new Map(prev)
      updated.forEach((nodeLabels, nodeId) => {
        const filtered = nodeLabels.filter((l) => l.id !== labelId)
        if (filtered.length !== nodeLabels.length) {
          updated.set(nodeId, filtered)
        }
      })
      return updated
    })

    // Increment version to trigger re-renders
    setNodeLabelsVersion((prev) => prev + 1)

    notificationRef.current.success({
      title: 'Label deleted',
      description: 'The label has been removed from the project and all nodes',
    })

    return true
  }, [])

  /**
   * Set labels for a specific node in cache
   */
  const setNodeLabels = useCallback((nodeId: string, nodeLabels: ProjectLabel[]) => {
    setNodeLabelsMap((prev) => {
      const newMap = new Map(prev)
      newMap.set(nodeId, nodeLabels)
      return newMap
    })
    setNodeLabelsVersion((prev) => prev + 1)
  }, [])

  /**
   * Set labels for multiple nodes at once (batch update to minimize re-renders)
   */
  const setNodeLabelsBatch = useCallback(
    (updates: Array<{ nodeId: string; labels: ProjectLabel[] }>) => {
      setNodeLabelsMap((prev) => {
        const newMap = new Map(prev)
        for (const { nodeId, labels } of updates) {
          newMap.set(nodeId, labels)
        }
        return newMap
      })
      setNodeLabelsVersion((prev) => prev + 1)
    },
    []
  )

  /**
   * Reload labels for a specific node from the server
   */
  const reloadNodeLabels = useCallback(
    async (mindmapId: string, nodeId: string) => {
      try {
        const { data, error } = await LabelService.getNodeLabels(mindmapId, nodeId)
        if (!error && data) {
          const labelsFromServer = data.labels
          setNodeLabels(nodeId, labelsFromServer)
          // Also ensure all labels from server are in the project labels list
          // This handles the case where a label was just created
          setLabels((prev) => {
            const existingIds = new Set(prev.map((l) => l.id))
            const newLabels = labelsFromServer.filter((l) => !existingIds.has(l.id))
            if (newLabels.length > 0) {
              return [...prev, ...newLabels]
            }
            return prev
          })
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'label', action: 'reload_node_labels' },
        })
        // Silently fail - this is a best-effort sync
      }
    },
    [setNodeLabels]
  )

  /**
   * Add a label to a node
   */
  const addLabelToNode = useCallback(
    async (mindmapId: string, nodeId: string, labelId: string): Promise<boolean> => {
      const { data, error } = await LabelService.addLabelToNode(mindmapId, nodeId, labelId)

      if (error) {
        // If it's a 409 conflict or "already assigned", reload labels from server to sync state
        if (
          error.status === 409 ||
          error.message.includes('already assigned') ||
          error.message.includes('already exists')
        ) {
          // Label is already assigned, reload from server to sync state
          await reloadNodeLabels(mindmapId, nodeId)
          return true // Return true since the label is already there
        }
        // Show error for other cases
        notificationRef.current.error({
          title: 'Failed to add label',
          description: error.message,
        })
        return false
      }

      if (data && data.success) {
        // After successfully adding a label, reload node labels from server to ensure we have the latest state
        // This is important because:
        // 1. The label might have been just created and not in the labels array yet
        // 2. We want to ensure cache is in sync with server
        await reloadNodeLabels(mindmapId, nodeId)
        return true
      }

      return false
    },
    [reloadNodeLabels]
  )

  /**
   * Remove a label from a node
   */
  const removeLabelFromNode = useCallback(
    async (mindmapId: string, nodeId: string, labelId: string): Promise<boolean> => {
      const { data, error } = await LabelService.removeLabelFromNode(mindmapId, nodeId, labelId)

      if (error) {
        notificationRef.current.error({
          title: 'Failed to remove label',
          description: error.message,
        })
        return false
      }

      if (data && data.success) {
        // Update cache: remove label from node's labels
        setNodeLabelsMap((prev) => {
          const newMap = new Map(prev)
          const currentLabels = newMap.get(nodeId) ?? []
          newMap.set(
            nodeId,
            currentLabels.filter((l) => l.id !== labelId)
          )
          return newMap
        })
        setNodeLabelsVersion((prev) => prev + 1)
        return true
      }

      return false
    },
    []
  )

  /**
   * Get labels for a specific node from cache
   */
  const getNodeLabels = useCallback(
    (nodeId: string): ProjectLabel[] => {
      return nodeLabelsMap.get(nodeId) ?? []
    },
    [nodeLabelsMap]
  )

  // Memoize context value to prevent re-renders of all consumers on every parent render
  const value: LabelContextType = useMemo(
    () => ({
      labels,
      isLoading,
      loadLabels,
      setLabelsDirectly,
      createLabel,
      deleteLabel,
      addLabelToNode,
      removeLabelFromNode,
      getNodeLabels,
      setNodeLabels,
      setNodeLabelsBatch,
      reloadNodeLabels,
      nodeLabelsVersion,
    }),
    [
      labels,
      isLoading,
      loadLabels,
      setLabelsDirectly,
      createLabel,
      deleteLabel,
      addLabelToNode,
      removeLabelFromNode,
      getNodeLabels,
      setNodeLabels,
      setNodeLabelsBatch,
      reloadNodeLabels,
      nodeLabelsVersion,
    ]
  )

  return <LabelContext.Provider value={value}>{children}</LabelContext.Provider>
}
